// Request submission page. The 4-step wizard collects the request body and
// posts to either /api/requests (when at least one specific point is given
// via the typeahead or URL preset) or /api/area-requests (when only an
// address is given). The connector contact is treated as the same person
// as the organisation contact in v1 - designer's form is single-party so
// we dedupe on the backend side via matching email addresses.
//
// Multi-point: the user can add N stations to one request via the search
// typeahead. The first pick becomes the request's primary point; the rest
// get sent as `additionalPoints` and the backend creates one tentative
// booking per station, all sharing the project window + approval flow.
import './site';
import { initWizard } from './site';
import { loadConfig } from '../envConfig';
import { api } from './api';
import { t } from './i18n/i18n';
import { friendlyError } from './friendlyError';
import { fetchConnectorCatalogue, populateConnectorSelect } from './connectorSelect';
import { setMinToday } from './lib/dateRange';

void loadConfig();
setMinToday('f-start', 'f-end');

// Populate the connector-type dropdown from the catalogue (the wizard has no
// per-station connector data client-side). "Unknown" is always available.
void (async () => {
  const sel = document.getElementById('f-connector') as HTMLSelectElement | null;
  if (!sel) return;
  populateConnectorSelect(sel, [], await fetchConnectorCatalogue(), {
    placeholder: t('aanvraag.wizard.connector_default'),
    unknown: t('aanvraag.wizard.connector_unknown'),
  });
})();

interface PointPick { kind: string; id: number; label?: string }

interface MapSearchHit {
  label: string;
  latitude: number;
  longitude: number;
  typecat: string;
  pointKind: string;
  pointId: number;
}

const params = new URL(window.location.href).searchParams;
const presetKind = params.get('kind');
const presetId = params.get('id');

const searchInput   = document.getElementById('f-pointsearch') as HTMLInputElement | null;
const searchResults = document.getElementById('f-pointsearch-results') as HTMLDivElement | null;
const stationsField = document.getElementById('selected-stations-field') as HTMLDivElement | null;
const stationsHost  = document.getElementById('selected-stations') as HTMLDivElement | null;

const picks: PointPick[] = [];

function pickKey(p: PointPick): string {
  return `${p.kind}-${p.id}`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function renderPicks(): void {
  if (!stationsField || !stationsHost) return;
  stationsField.hidden = picks.length === 0;
  if (picks.length === 0) { stationsHost.innerHTML = ''; return; }
  const removeLbl = t('common.btn.close') || 'Verwijderen';
  stationsHost.innerHTML = picks.map((p) => {
    const chipText = p.label && p.label.trim().length > 0 ? p.label : pickKey(p);
    return `
      <span class="selected-stations__chip" data-key="${escapeHtml(pickKey(p))}"
            style="display:inline-flex;align-items:center;gap:6px;background:var(--sk-blue-100,#eff3fb);
                   border:1px solid var(--sk-border,#d6dce6);border-radius:20px;padding:5px 10px 5px 12px;
                   font-size:12.5px;color:var(--sk-navy-800,#0a2a5c);">
        <span style="font-weight:600;">${escapeHtml(chipText)}</span>
        <button type="button" class="selected-stations__remove" data-remove-key="${escapeHtml(pickKey(p))}"
                aria-label="${escapeHtml(removeLbl)}"
                style="background:transparent;border:0;cursor:pointer;color:var(--sk-muted);font-size:16px;line-height:1;padding:0 2px;">×</button>
      </span>`;
  }).join('');
  stationsHost.querySelectorAll<HTMLButtonElement>('button[data-remove-key]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.removeKey;
      const idx = picks.findIndex((p) => pickKey(p) === key);
      if (idx >= 0) { picks.splice(idx, 1); renderPicks(); }
    });
  });
}

function addPick(p: PointPick): boolean {
  if (picks.some((existing) => existing.kind === p.kind && existing.id === p.id)) return false;
  picks.push(p);
  renderPicks();
  clearError();
  return true;
}

// Pre-seed from URL (no label available - the chip falls back to the key).
if (presetKind && presetId) {
  const id = Number(presetId);
  if (Number.isFinite(id) && id > 0) addPick({ kind: presetKind, id });
}

function hideResults(): void {
  if (!searchResults) return;
  searchResults.innerHTML = '';
  searchResults.hidden = true;
}

function showResultsMessage(message: string): void {
  if (!searchResults) return;
  searchResults.hidden = false;
  searchResults.innerHTML = `
    <div style="padding:10px 14px;color:var(--sk-muted,#666);font-size:13px;">
      ${escapeHtml(message)}
    </div>`;
}

function renderResults(hits: MapSearchHit[]): void {
  if (!searchResults) return;
  if (hits.length === 0) {
    showResultsMessage(t('aanvraag.wizard.pointsearch_no_results') || 'Geen stroompunten gevonden.');
    return;
  }
  searchResults.hidden = false;
  searchResults.innerHTML = hits.map((hit, idx) => {
    const alreadyPicked = picks.some((p) => p.kind === hit.pointKind && p.id === hit.pointId);
    return `
      <button type="button" data-hit-idx="${idx}"
              ${alreadyPicked ? 'disabled' : ''}
              style="display:block;width:100%;text-align:left;padding:10px 14px;background:#fff;
                     border:0;border-bottom:1px solid var(--sk-border,#eee);cursor:${alreadyPicked ? 'default' : 'pointer'};
                     font-size:13.5px;color:var(--sk-navy-800,#0a2a5c);opacity:${alreadyPicked ? '0.55' : '1'};">
        <div style="font-weight:600;">${escapeHtml(hit.label || `#${hit.pointId}`)}</div>
        <div style="font-size:11.5px;color:var(--sk-muted,#666);margin-top:2px;">
          ${escapeHtml(hit.typecat)}${alreadyPicked ? ' · ✓' : ''}
        </div>
      </button>`;
  }).join('');
  searchResults.querySelectorAll<HTMLButtonElement>('button[data-hit-idx]').forEach((btn) => {
    btn.addEventListener('mousedown', (e) => { e.preventDefault(); }); // keep input focus
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const idx = Number(btn.dataset.hitIdx);
      const hit = hits[idx];
      if (!hit) return;
      addPick({ kind: hit.pointKind, id: hit.pointId, label: hit.label });
      if (searchInput) searchInput.value = '';
      hideResults();
      searchInput?.focus();
    });
  });
}

let searchTimer: number | undefined;
let searchSeq = 0;

async function runSearch(term: string): Promise<void> {
  const seq = ++searchSeq;
  if (term.length < 3) {
    showResultsMessage(t('aanvraag.wizard.pointsearch_min') || 'Typ minstens 3 letters…');
    return;
  }
  showResultsMessage(t('aanvraag.wizard.pointsearch_searching') || 'Zoeken…');
  try {
    const client = await api();
    const response = await client.get<MapSearchHit[]>('/api/map/search', { params: { q: term } });
    if (seq !== searchSeq) return; // stale
    renderResults(response.data || []);
  } catch (err) {
    if (seq !== searchSeq) return;
    console.error('[request.search]', err);
    showResultsMessage(t('aanvraag.wizard.pointsearch_no_results') || 'Geen stroompunten gevonden.');
  }
}

searchInput?.addEventListener('input', () => {
  const term = (searchInput.value || '').trim();
  window.clearTimeout(searchTimer);
  if (term.length === 0) { hideResults(); return; }
  searchTimer = window.setTimeout(() => { void runSearch(term); }, 250);
});

searchInput?.addEventListener('focus', () => {
  const term = (searchInput.value || '').trim();
  if (term.length >= 3) void runSearch(term);
});

searchInput?.addEventListener('blur', () => {
  // Delay so click on a suggestion (mousedown-prevented) still fires.
  window.setTimeout(hideResults, 150);
});

searchInput?.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { hideResults(); }
});

function valueOf(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)?.value?.trim() ?? '';
}

function showError(message: string): void {
  const head = document.querySelector<HTMLElement>('.wizard__panel-head');
  let box = document.getElementById('request-error') as HTMLElement | null;
  if (!box) {
    box = document.createElement('p');
    box.id = 'request-error';
    box.className = 'login-error';
    box.style.cssText = 'background:#fee2e2;color:#991b1b;padding:12px 16px;border-radius:8px;margin:12px 0;';
    head?.parentElement?.insertBefore(box, head?.nextSibling ?? null);
  }
  box.textContent = message;
  box.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function clearError(): void {
  const box = document.getElementById('request-error');
  if (box) box.remove();
}

initWizard('#wizard', {
  onSubmit: async () => {
    const projectName = valueOf('f-project');
    const organisation = valueOf('f-org');
    const startAt = valueOf('f-start');
    const endAt = valueOf('f-end');
    const contactName = valueOf('f-name');
    const contactEmail = valueOf('f-email');
    const contactPhone = valueOf('f-phone');
    const requestedConnectorType = valueOf('f-connector') || null;

    const baseBody = {
      projectName,
      organisation,
      requestedConnectorType,
      startAt: new Date(startAt).toISOString(),
      endAt: new Date(endAt).toISOString(),
      contactName,
      contactEmail,
      contactPhone: contactPhone || null,
      // V1 single-party: connector = contact, backend collapses dupe emails.
      connectorName: contactName,
      connectorEmail: contactEmail,
      connectorPhone: contactPhone || null,
    };

    try {
      const client = await api();
      let response;
      if (picks.length > 0) {
        const [primary, ...rest] = picks;
        response = await client.post<{ id: string; status: string }>('/api/requests', {
          pointKind: primary.kind,
          pointId: primary.id,
          additionalPoints: rest.map((p) => ({ pointKind: p.kind, pointId: p.id })),
          ...baseBody,
        });
      } else {
        // Area request: stash the address into a JSON polygon-placeholder
        // so the operator's resolution UI has something to work with.
        const polygon = JSON.stringify({
          type: 'AddressLookup',
          postalCode: valueOf('f-postal'),
          city: valueOf('f-city'),
          address: valueOf('f-address'),
        });
        response = await client.post<{ id: string; status: string }>('/api/area-requests', {
          areaPolygon: polygon,
          ...baseBody,
        });
      }
      window.location.href = `request-submitted.html?id=${encodeURIComponent(response.data.id)}`;
    } catch (err) {
      showError(friendlyError(err));
      throw err; // Keeps the wizard on the current panel via initWizard's try/catch.
    }
  },
});
